import { createContext, ReactNode, useContext } from "react";

/**
 * Ambient (table, sheet, column) pointer exposed to any Smart-Table-aware
 * PowerBlock rendered inside the column-config sidebar.
 *
 * ## Why a context?
 *
 * The Composition block needs to know three things that are owned by the
 * sidebar, not the block itself:
 *
 *   1. The **current workspace's smart-table id** (`tableId`). Composition
 *      has a "This table" shortcut that pre-selects the current table
 *      without the user going through a chooser. Without this context the
 *      block would have no way to know what "this table" means.
 *   2. The **current sheet id** (`sheetId`). Same-table composition
 *      typically filters the target sheet list to "everything except the
 *      current sheet" so a trivial self-reference can't be created; the
 *      block also uses this to short-circuit the cross-table discovery
 *      call when the user stays in the current table.
 *   3. The **current column id** (`columnId`). The backend rejects
 *      self-reference via a DB check constraint
 *      (`ck_smart_column_dependencies_no_self_ref`). Excluding the current
 *      column from the source-column picker avoids a save-then-error
 *      round-trip and lets the dropdown render a narrower list.
 *
 * Threading these three ids as props through every block definition and
 * every `BlockProps` consumer would require touching a lot of surface
 * area that has nothing to do with composition. A context lets the
 * sidebar set them once at the top of the subtree and only the blocks
 * that care (today: Composition) read them.
 *
 * ## Nullability
 *
 * All three fields are optional. Non-Smart-Table call sites never install
 * the provider, so consumers always go through `useColumnTargetContext()`
 * which returns an empty record in that case. The Composition block
 * guards on each field individually (e.g. if `tableId` is missing, it
 * falls back to the "Another table" flow and requires the user to pick a
 * target explicitly).
 */
export interface ColumnTargetContextValue {
  /**
   * The id of the smart table that contains the column currently being
   * edited in the sidebar. Used by the Composition block to enable the
   * "This table" shortcut and to produce the `currentTableId` argument
   * for `useCompositeColumnSources`.
   */
  tableId?: string;
  /**
   * The id of the sheet that contains the column currently being edited.
   * Passed to `useListAvailableSources` as `exclude_sheet_id` so the
   * discovery endpoint never returns the current sheet (which would allow
   * building a trivial same-sheet self-reference through the cross-sheet
   * path).
   */
  sheetId?: string;
  /**
   * The id of the column currently being edited. The Composition block
   * filters this id out of the source-column picker so the UI can't
   * propose a self-reference that the backend would later reject.
   */
  columnId?: string;
}

const ColumnTargetContext = createContext<ColumnTargetContextValue | null>(null);

/**
 * Wrap a subtree to expose the current (table, sheet, column) pointer to
 * any nested Smart-Table-aware block (currently just Composition).
 *
 * Installed once, near the top of `ColumnConfigSidebar`, so every
 * rendered `Block` child automatically has the ids available without
 * needing bespoke prop plumbing in each block definition.
 *
 * The provider always renders — even when every field is `undefined` —
 * so descendants consistently see a context value. `useColumnTargetContext`
 * then returns an empty object, which blocks can treat as the "no
 * ambient target" case.
 */
export const ColumnTargetProvider = ({
  value,
  children,
}: {
  value: ColumnTargetContextValue;
  children: ReactNode;
}) => <ColumnTargetContext.Provider value={value}>{children}</ColumnTargetContext.Provider>;

/**
 * Read the ambient (table, sheet, column) pointer. Returns an empty object
 * outside of a `ColumnTargetProvider` (e.g. in non-Smart-Table
 * renderings of the PowerBlock system) so callers can destructure without
 * a null-check.
 *
 * Prefer this over `useContext(ColumnTargetContext)` directly — the
 * empty-object fallback keeps individual fields optional and lets the
 * consumer treat "outside a provider" and "inside a provider but field
 * unset" identically.
 */
export const useColumnTargetContext = (): ColumnTargetContextValue =>
  useContext(ColumnTargetContext) ?? {};
